import { CityContext } from "@/contexts/city-context-provider";
import useToggle from "@/hooks/use-toggle";
import {
  applyOpacityToRgba,
  getPolygonsFromGeometry,
  multiPolygonToPolygons,
  overlapErrorMessage,
  parkingPaymentGeoJsonLayerModes,
  paymentZoneAreaMode,
  polygonsToMultiPolygon,
  toastError,
} from "@/modules/parking-payment/parking-payment.const";
import {
  CreatePaymentZoneAreaInput,
  ParkingPaymentModeType,
  PaymentZoneArea,
  SavePaymentZoneAreaPayload,
  UpdatePaymentZoneAreaInput,
} from "@/modules/parking-payment/payment-zone-area.type";
import {
  FeatureCollection as DeckFeatureCollection,
  EditableGeoJsonLayer,
  EditAction,
} from "@deck.gl-community/editable-layers";
import { area, intersect } from "@turf/turf";
import { Color, GeoJsonLayer, PickingInfo } from "deck.gl";
import type { FeatureCollection, MultiPolygon, Polygon } from "geojson";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  useCreatePaymentZoneArea,
  useGetPaymentZoneAreas,
  useUpdatePaymentZoneArea,
} from "./use-payment-zone-area-query";

const hasRealOverlap = (a: Polygon, b: Polygon): boolean => {
  const intersection = intersect({
    type: "FeatureCollection",
    features: [
      { type: "Feature", geometry: a, properties: {} },
      { type: "Feature", geometry: b, properties: {} },
    ],
  });
  return intersection !== null && area(intersection) > 0;
};

type UsePaymentZoneAreaParams = {
  appMode: ParkingPaymentModeType;
  hiddenZoneIds?: Set<string>;
  draftColor?: [number, number, number, number];
};

export const usePaymentZoneArea = ({ appMode, hiddenZoneIds, draftColor }: UsePaymentZoneAreaParams) => {
  const { currentCity } = useContext(CityContext);
  const [openPaymentZoneAreaModal, setPaymentZoneAreaModal] = useToggle(false);
  const [paymentZoneAreaDetail, setPaymentZoneAreaDetail] = useState<PaymentZoneArea>();
  const [paymentZoneAreaEditionData, setPaymentAreaZoneEditionData] = useState<FeatureCollection>({
    type: "FeatureCollection",
    features: [],
  });
  const [hoveredFeatureId, setHoveredFeatureId] = useState<string | undefined>(undefined);
  const [isEditingGeometry, setIsEditingGeometry] = useState(false);

  const { createPaymentArea } = useCreatePaymentZoneArea();
  const { updatePaymentArea } = useUpdatePaymentZoneArea();

  const { paymentZoneAreas, loading: paymentZoneAreaLoading } = useGetPaymentZoneAreas(currentCity?.id ?? "");

  const visiblePaymentZoneAreaEditionData = useMemo<FeatureCollection>(() => {
    if (!hiddenZoneIds?.size) {
      return paymentZoneAreaEditionData;
    }

    return {
      type: paymentZoneAreaEditionData.type,
      features: paymentZoneAreaEditionData.features.filter((feature) => {
        const featureId = feature?.id?.toString();
        if (!featureId) {
          return true;
        }
        return !hiddenZoneIds.has(featureId);
      }),
    };
  }, [hiddenZoneIds, paymentZoneAreaEditionData]);

  const showPaymentZoneAreaDetail = useMemo(
    () =>
      appMode === ParkingPaymentModeType.EditPaymentZoneArea &&
      paymentZoneAreaDetail &&
      Object.keys(paymentZoneAreaDetail).length > 0,
    [appMode, paymentZoneAreaDetail],
  );

  const editableGeoJsonLayerMode = useMemo(() => parkingPaymentGeoJsonLayerModes[appMode], [appMode]);

  const isVisiblePaymentZoneAreaLayer = useMemo(() => {
    return (
      appMode === ParkingPaymentModeType.AddPaymentZoneArea || appMode === ParkingPaymentModeType.EditPaymentZoneArea
    );
  }, [appMode]);

  const [selectedFeatureIndexes, setSelectedFeatureIndexes] = useState<number[]>([]);
  const [selectedPolygonIndex, setSelectedPolygonIndex] = useState<number | null>(null);

  const findOverlappingZone = useCallback(
    (target: Polygon | MultiPolygon, excludeId?: string) => {
      const targetPolygons = getPolygonsFromGeometry(target);
      const others = paymentZoneAreaEditionData.features.filter(
        (f) => f.id && (!excludeId || f?.id?.toString() !== excludeId),
      );

      return (
        others.find((other) => {
          const otherPolygons = getPolygonsFromGeometry(other.geometry as Polygon | MultiPolygon);
          return targetPolygons.some((tp) => otherPolygons.some((op) => hasRealOverlap(tp, op)));
        }) ?? null
      );
    },
    [paymentZoneAreaEditionData.features],
  );

  const onEditPaymentZoneAreaLayer = useCallback(
    ({ updatedData, editType }: EditAction<FeatureCollection<Polygon, PaymentZoneArea>>) => {
      const updatedPolygons = updatedData.features.map((f) => f.geometry as Polygon);

      if (appMode === ParkingPaymentModeType.EditPaymentZoneArea && paymentZoneAreaDetail) {
        const updatedMultiPolygon = polygonsToMultiPolygon(updatedPolygons);

        const terminalEditTypes = ["finishMovePosition", "addPosition", "removePosition"];
        if (terminalEditTypes.includes(editType)) {
          const overlapping = findOverlappingZone(updatedMultiPolygon, paymentZoneAreaDetail.id);

          if (overlapping) {
            toastError(overlapErrorMessage(overlapping));
            return;
          }
        }

        setPaymentAreaZoneEditionData((current) => ({
          ...current,
          features: current.features.map((f) =>
            f?.id?.toString() === paymentZoneAreaDetail.id ? { ...f, geometry: updatedMultiPolygon } : f,
          ),
        }));
      } else if (appMode === ParkingPaymentModeType.AddPaymentZoneArea) {
        if (editType === "addFeature") {
          const newPolygon = updatedData.features.at(-1);

          if (newPolygon) {
            const overlapping = findOverlappingZone(newPolygon.geometry as Polygon, paymentZoneAreaDetail?.id);

            if (overlapping) {
              toastError(overlapErrorMessage(overlapping));
              return;
            }
          }
        }

        const nextData = {
          ...updatedData,
          features: updatedData.features.map((feature) => {
            if (feature.id) return feature;
            return {
              ...feature,
              properties: {
                ...(feature.properties ?? {}),
                color: draftColor,
              },
            };
          }),
        };
        setPaymentAreaZoneEditionData((current) => ({
          ...current,
          features: [...current.features.filter((f) => f.id), ...nextData.features.filter((f) => !f.id)],
        }));
      }
    },
    [appMode, paymentZoneAreaDetail, draftColor, findOverlappingZone, setPaymentAreaZoneEditionData],
  );

  const onClickPaymentZoneAreaLayer = useCallback(
    (info: PickingInfo) => {
      if (info.object && appMode === ParkingPaymentModeType.EditPaymentZoneArea) {
        if (info.object.id === paymentZoneAreaDetail?.id) {
          setSelectedPolygonIndex(info.index);
          return;
        }
        if (!info.object.id) {
          return;
        }
        setPaymentZoneAreaDetail(info.object);
        setSelectedPolygonIndex(null);
      } else {
        setPaymentZoneAreaDetail(undefined);
        setSelectedPolygonIndex(null);
      }
    },
    [appMode, paymentZoneAreaDetail?.id, setPaymentZoneAreaDetail],
  );

  const onHoverPaymentZoneAreaLayer = useCallback((info: PickingInfo) => {
    if (info.object?.id) {
      setHoveredFeatureId(info.object.id.toString());
    } else {
      setHoveredFeatureId(undefined);
    }
  }, []);

  // Reveal every vertex handle of the zone being edited so vertices are click-removable on entry.
  useEffect(() => {
    setSelectedPolygonIndex(null);
    if (appMode === ParkingPaymentModeType.EditPaymentZoneArea && paymentZoneAreaDetail) {
      const count = getPolygonsFromGeometry(paymentZoneAreaDetail.geometry as Polygon | MultiPolygon).length;
      setSelectedFeatureIndexes(Array.from({ length: count }, (_, index) => index));
      setIsEditingGeometry(count > 0);
      return;
    }
    setIsEditingGeometry(false);
    setSelectedFeatureIndexes([]);
  }, [paymentZoneAreaDetail, appMode]);

  const selectedZonePolygonCount = useMemo(() => {
    if (!paymentZoneAreaDetail) return 0;
    const zoneFeature = paymentZoneAreaEditionData.features.find((f) => f?.id?.toString() === paymentZoneAreaDetail.id);
    return zoneFeature ? getPolygonsFromGeometry(zoneFeature.geometry as Polygon | MultiPolygon).length : 0;
  }, [paymentZoneAreaDetail, paymentZoneAreaEditionData.features]);

  const canRemoveSelectedPolygon = selectedPolygonIndex !== null && selectedZonePolygonCount > 1;

  const removeSelectedPolygon = useCallback(() => {
    if (selectedPolygonIndex === null || !paymentZoneAreaDetail) return;
    const zoneFeature = paymentZoneAreaEditionData.features.find((f) => f?.id?.toString() === paymentZoneAreaDetail.id);
    if (!zoneFeature) return;

    const polygons = getPolygonsFromGeometry(zoneFeature.geometry as Polygon | MultiPolygon);
    if (polygons.length <= 1) return;

    const updatedGeometry = polygonsToMultiPolygon(polygons.filter((_, index) => index !== selectedPolygonIndex));
    setPaymentAreaZoneEditionData((current) => ({
      ...current,
      features: current.features.map((f) =>
        f?.id?.toString() === paymentZoneAreaDetail.id ? { ...f, geometry: updatedGeometry } : f,
      ),
    }));
    setSelectedPolygonIndex(null);
    setSelectedFeatureIndexes(Array.from({ length: polygons.length - 1 }, (_, index) => index));
  }, [selectedPolygonIndex, paymentZoneAreaDetail, paymentZoneAreaEditionData.features, setPaymentAreaZoneEditionData]);

  const savePaymentZoneAreaProperties = async ({ properties, geometry, zoneId }: SavePaymentZoneAreaPayload = {}) => {
    const editingZoneId = zoneId || paymentZoneAreaDetail?.id;

    if (editingZoneId) {
      const targetFeature = paymentZoneAreaEditionData.features.find((f) => f?.id?.toString() === editingZoneId);
      if (!targetFeature) return;

      const updatedPaymentZoneAreaParam = {
        ...targetFeature,
        ...(properties ?? {}),
        id: editingZoneId,
        cityId: currentCity?.id,
        geometry: geometry ?? targetFeature.geometry,
        properties: {
          ...(targetFeature.properties ?? {}),
          ...(properties?.color ? { color: properties.color } : {}),
          ...(properties?.name ? { name: properties.name } : {}),
        },
      } as UpdatePaymentZoneAreaInput;

      await updatePaymentArea(updatedPaymentZoneAreaParam);
    } else {
      // Mode création
      const lastPaymentZoneAreaFeature = paymentZoneAreaEditionData.features.at(-1);
      const resolvedGeometry = geometry ?? lastPaymentZoneAreaFeature?.geometry;
      if (!resolvedGeometry) return;

      const createPaymentZoneAreaParam = {
        ...(properties ?? {}),
        cityId: currentCity?.id,
        geometry: resolvedGeometry,
        properties: {
          ...(lastPaymentZoneAreaFeature?.properties ?? {}),
          ...(properties?.color ? { color: properties.color } : {}),
          ...(properties?.name ? { name: properties.name } : {}),
        },
      } as CreatePaymentZoneAreaInput;

      await createPaymentArea(createPaymentZoneAreaParam);
    }
    if (openPaymentZoneAreaModal) {
      setPaymentZoneAreaModal();
    }
  };

  const zone = useMemo(
    () =>
      paymentZoneAreaEditionData.features
        .map((feature) => {
          if (feature.geometry.type === "Polygon") {
            return [
              {
                ...feature,
                type: "Feature",
                geometry: feature.geometry,
                polygonIndex: 0,
              },
            ];
          }
          const polygons = multiPolygonToPolygons(feature.geometry as MultiPolygon);
          return polygons.map((polygon, polygonIndex) => ({
            ...feature,
            type: "Feature",
            geometry: polygon,
            polygonIndex,
          }));
        })
        .flat(),
    [paymentZoneAreaEditionData.features],
  );

  // Determine which features should be visible in the editable layer
  const editableFeatures = useMemo(() => {
    // show only the polygons of the selected zone
    if (appMode === ParkingPaymentModeType.EditPaymentZoneArea && paymentZoneAreaDetail) {
      return {
        type: "FeatureCollection",
        features: zone.filter((f) => f?.id?.toString() === paymentZoneAreaDetail.id),
      };
    }

    // Add mode with existing zone selected: show both existing zone polygons and new drafts
    if (appMode === ParkingPaymentModeType.AddPaymentZoneArea && paymentZoneAreaDetail) {
      return {
        type: "FeatureCollection",
        features: [...zone.filter((f) => f?.id?.toString() === paymentZoneAreaDetail.id), ...zone.filter((f) => !f.id)],
      };
    }

    // Add mode without zone selected: show only new draft polygons
    if (appMode === ParkingPaymentModeType.AddPaymentZoneArea) {
      return { type: "FeatureCollection", features: zone.filter((f) => !f.id) };
    }
    return { type: "FeatureCollection", features: zone };
  }, [appMode, paymentZoneAreaDetail, zone]);

  // Layer definitions
  const editablePaymentZoneAreaLayer = useMemo(
    () =>
      new EditableGeoJsonLayer({
        id: "payment-zone-areas-layer",
        data: editableFeatures as DeckFeatureCollection,
        mode: editableGeoJsonLayerMode,
        selectedFeatureIndexes: isEditingGeometry && paymentZoneAreaDetail ? selectedFeatureIndexes : [],
        getFillColor: (feature: PaymentZoneArea, { index }: { index: number }) =>
          applyOpacityToRgba(feature?.properties?.color, index === selectedPolygonIndex ? 0.55 : 0.3),
        getLineColor: (feature: PaymentZoneArea) => {
          const isSelected = feature?.id === paymentZoneAreaDetail?.id;
          return applyOpacityToRgba(feature?.properties?.color, isSelected ? 1 : 0.8);
        },
        getLineWidth: 2.7,
        pickable: true,
        getEditHandlePointRadius: 8,
        getEditHandlePointColor: () => [255, 255, 255, 255],
        getEditHandlePointOutlineColor: () => [34, 197, 94, 255],
        getTentativeFillColor: (feature: PaymentZoneArea) =>
          applyOpacityToRgba(feature?.properties?.color || draftColor, 0.2),
        getTentativeLineColor: (feature: PaymentZoneArea) =>
          applyOpacityToRgba(feature?.properties?.color || draftColor, 0.8),
        onEdit: (updatedData) =>
          onEditPaymentZoneAreaLayer(updatedData as EditAction<FeatureCollection<Polygon, PaymentZoneArea>>),
        onClick: onClickPaymentZoneAreaLayer,
        onHover: onHoverPaymentZoneAreaLayer,
        visible: isVisiblePaymentZoneAreaLayer,
        updateTriggers: {
          data: [appMode, paymentZoneAreaDetail, paymentZoneAreaEditionData.features.length],
          getFillColor: [hoveredFeatureId, selectedPolygonIndex],
          getLineColor: [paymentZoneAreaDetail],
          getLineWidth: [hoveredFeatureId],
          selectedFeatureIndexes: [appMode, paymentZoneAreaDetail],
          getTentativeFillColor: [draftColor],
          getTentativeLineColor: [draftColor],
        },
      }),
    [
      paymentZoneAreaEditionData.features.length,
      editableFeatures,
      editableGeoJsonLayerMode,
      isEditingGeometry,
      paymentZoneAreaDetail,
      selectedFeatureIndexes,
      onEditPaymentZoneAreaLayer,
      onClickPaymentZoneAreaLayer,
      onHoverPaymentZoneAreaLayer,
      isVisiblePaymentZoneAreaLayer,
      appMode,
      hoveredFeatureId,
      draftColor,
      selectedPolygonIndex,
    ],
  );

  const paymentZoneLayer = useMemo(
    () =>
      new GeoJsonLayer({
        id: "payment-zone-area-layer",
        data: {
          type: visiblePaymentZoneAreaEditionData.type,
          features: visiblePaymentZoneAreaEditionData.features.filter(
            (f) => !paymentZoneAreaDetail || f?.id?.toString() !== paymentZoneAreaDetail.id,
          ),
        },
        pickable: paymentZoneAreaMode.includes(appMode),
        autoHighlight: false,
        getLineColor: ((feature: PaymentZoneArea) => {
          const isHovered = feature?.id?.toString() === hoveredFeatureId;
          return applyOpacityToRgba(feature?.properties?.color, isHovered ? 3 : 1);
        }) as unknown as Color,
        getFillColor: ((feature: PaymentZoneArea) => {
          const isHovered = feature?.id?.toString() === hoveredFeatureId;
          return applyOpacityToRgba(feature?.properties?.color, isHovered ? 0.6 : 0.3);
        }) as unknown as Color,
        getLineWidth: ((feature: PaymentZoneArea) => {
          const isHovered = feature?.id?.toString() === hoveredFeatureId;
          return isHovered ? 2.5 : 0.5;
        }) as unknown as number,
        onHover: onHoverPaymentZoneAreaLayer,
        visible: true,
        updateTriggers: {
          data: [paymentZoneAreaDetail, paymentZoneAreaEditionData.features.length],
          getFillColor: [hoveredFeatureId],
          pickable: [appMode],
        },
      }),
    [
      visiblePaymentZoneAreaEditionData,
      paymentZoneAreaDetail,
      hoveredFeatureId,
      onHoverPaymentZoneAreaLayer,
      paymentZoneAreaEditionData.features.length,
      appMode,
    ],
  );

  const paymentZoneAreaLayers = useMemo(() => {
    if (paymentZoneAreaMode.includes(appMode)) {
      return [editablePaymentZoneAreaLayer, paymentZoneLayer];
    }
    return [paymentZoneLayer];
  }, [appMode, editablePaymentZoneAreaLayer, paymentZoneLayer]);

  // Update draft polygon colors when draftColor changes
  useEffect(() => {
    if (appMode !== ParkingPaymentModeType.AddPaymentZoneArea || !draftColor) {
      return;
    }

    const hasDraftFeatures = paymentZoneAreaEditionData.features.some((f) => !f.id);
    if (!hasDraftFeatures) {
      return;
    }

    setPaymentAreaZoneEditionData((prev) => ({
      ...prev,
      features: prev.features.map((feature) =>
        !feature.id
          ? {
              ...feature,
              properties: {
                ...(feature.properties ?? {}),
                color: draftColor,
              },
            }
          : feature,
      ),
    }));
  }, [appMode, draftColor, paymentZoneAreaEditionData.features, paymentZoneAreaEditionData.features.length]);

  useEffect(() => {
    if (!currentCity || paymentZoneAreaLoading) {
      return;
    }

    const normalizedFeatures = paymentZoneAreas.map((feature) => {
      const properties = {
        ...(feature.properties ?? {}),
        ...(feature.name ? { name: feature.name } : {}),
        ...(feature.color ? { color: feature.color } : {}),
      };

      return {
        ...feature,
        properties,
      };
    });

    setPaymentAreaZoneEditionData({
      type: "FeatureCollection",
      features: normalizedFeatures,
    });
  }, [paymentZoneAreas, paymentZoneAreaLoading, currentCity]);

  return {
    openPaymentZoneAreaModal,
    setPaymentZoneAreaModal,
    paymentZoneAreaDetail,
    paymentZoneAreaEditionData,
    showPaymentZoneAreaDetail,
    setPaymentAreaZoneEditionData,
    setPaymentZoneAreaDetail,
    paymentZoneAreaLayers,
    savePaymentZoneAreaProperties,
    hoveredFeatureId,
    findOverlappingZone,
    canRemoveSelectedPolygon,
    removeSelectedPolygon,
  };
};
